import axios from 'axios';

const API_BASE_URL = 'https://api.lolocars.com/api';

// Create axios instance for analytics
const analyticsApi = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token for protected routes
analyticsApi.interceptors.request.use((config) => {
  const token = localStorage.getItem('spadmin_token');
  if (token && config.url?.includes('/analytics/') && !config.url?.includes('/track')) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Analytics Event Types
export type EventType = 'car_view' | 'whatsapp_click' | 'search';

export interface EventData {
  type: EventType;
  car_id?: number;
  view_duration?: number;
  language?: 'tr' | 'ar' | 'es' | 'uk' | 'ru';
  phone_number?: string;
  search_filters?: any;
  result_count?: number;
  selected_car_id?: number;
  page_url: string;
  referrer: string;
  device_type: 'desktop' | 'mobile' | 'tablet';
  browser_family: string;
  country_code: string;
  timestamp: Date;
}

export interface TrackEventRequest {
  events: EventData[];
}

export interface DailyMetrics {
  date: string;
  total_views: number;
  unique_visitors: number;
  whatsapp_clicks: number;
  carfax_requests: number;
  conversion_rate: number;
  avg_view_duration: number;
  bounce_rate: number;
}

export interface TopCarMetric {
  car_id: number;
  car_name: string;
  car_brand: string;
  car_model: string;
  car_year: number;
  car_price: number;
  total_views: number;
  whatsapp_clicks: number;
  conversion_rate: number;
}

export interface HourlyMetric {
  hour: number;
  views: number;
  whatsapp_clicks: number;
  searches: number;
}

export interface DashboardMetrics {
  today: DailyMetrics;
  yesterday: DailyMetrics;
  this_week: DailyMetrics;
  last_week: DailyMetrics;
  this_month: DailyMetrics;
  last_month: DailyMetrics;
  top_cars: TopCarMetric[];
  hourly_data: HourlyMetric[];
}

export interface ReportFilters {
  start_date: string;
  end_date: string;
  car_ids?: number[];
  countries?: string[];
  device_types?: string[];
  languages?: string[];
  metrics?: string[];
}

export interface GeographicMetric {
  country_code: string;
  country_name: string;
  total_views: number;
  whatsapp_clicks: number;
  conversion_rate: number;
}

export interface DeviceMetric {
  device_type: string;
  total_views: number;
  whatsapp_clicks: number;
  conversion_rate: number;
  percentage: number;
}

export interface ReportData {
  filters: ReportFilters;
  summary: DailyMetrics;
  time_series: DailyMetrics[];
  top_cars: TopCarMetric[];
  geographic: GeographicMetric[];
  device_data: DeviceMetric[];
  hourly_data: HourlyMetric[];
}

export interface LogEntry {
  id: number;
  car_id: number;
  car_name: string;
  car_brand: string;
  created_at: string;
}

export interface CarLogEntry extends LogEntry {
  view_duration: number;
  device_type: string;
  country_code: string;
  page_url: string;
  referrer: string;
}

export interface WhatsAppLogEntry extends LogEntry {
  language: 'tr' | 'ar' | 'es' | 'uk' | 'ru';
  phone_number: string;
  device_type: string;
  country_code: string;
}

export interface CarFaxLogEntry extends LogEntry {
  name: string;
  email: string;
  phone: string;
  message: string;
}

export interface PaginatedResponse<T> {
  logs: T[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    total_pages: number;
  };
}

export interface CarWithAnalytics {
  id: number;
  car_name: string;
  car_brand: string;
  car_model: string;
  car_year: number;
  car_price: number;
  car_status: string;
  total_views: number;
  whatsapp_clicks: number;
  carfax_requests: number;
  conversion_rate: number;
  last_viewed?: string;
}

export interface CarManagementResponse {
  cars: CarWithAnalytics[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    total_pages: number;
  };
}

// Analytics API Service
export const analyticsService = {
  // Public tracking endpoint
  trackEvents: async (events: EventData[]): Promise<void> => {
    try {
      await analyticsApi.post('/analytics/track', { events });
    } catch (error) {
      // Error: Analytics tracking failed - silently ignore to avoid disrupting UX
      // Don't throw error to avoid disrupting user experience
    }
  },

  // Dashboard metrics (protected)
  getDashboardMetrics: async (): Promise<DashboardMetrics> => {
    const response = await analyticsApi.get('/analytics/dashboard');
    return response.data.data;
  },

  // Analytics report (protected)
  getAnalyticsReport: async (filters: Partial<ReportFilters>): Promise<ReportData> => {
    const params = new URLSearchParams();
    
    if (filters.start_date) params.append('start_date', filters.start_date);
    if (filters.end_date) params.append('end_date', filters.end_date);
    if (filters.car_ids && filters.car_ids.length > 0) {
      params.append('car_ids', filters.car_ids.join(','));
    }
    if (filters.countries && filters.countries.length > 0) {
      params.append('countries', filters.countries.join(','));
    }
    if (filters.device_types && filters.device_types.length > 0) {
      params.append('device_types', filters.device_types.join(','));
    }

    const response = await analyticsApi.get(`/analytics/report?${params.toString()}`);
    return response.data.data;
  },

  // Car logs (protected)
  getCarLogs: async (
    page: number = 1,
    limit: number = 20,
    filters: {
      start_date?: string;
      end_date?: string;
      car_id?: string;
    } = {}
  ): Promise<PaginatedResponse<CarLogEntry>> => {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
    });

    if (filters.start_date) params.append('start_date', filters.start_date);
    if (filters.end_date) params.append('end_date', filters.end_date);
    if (filters.car_id) params.append('car_id', filters.car_id);

    const response = await analyticsApi.get(`/analytics/car-logs?${params.toString()}`);
    return response.data.data;
  },

  // WhatsApp logs (protected)
  getWhatsAppLogs: async (
    page: number = 1,
    limit: number = 20,
    filters: {
      start_date?: string;
      end_date?: string;
      language?: string;
      car_id?: string;
    } = {}
  ): Promise<PaginatedResponse<WhatsAppLogEntry>> => {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
    });

    if (filters.start_date) params.append('start_date', filters.start_date);
    if (filters.end_date) params.append('end_date', filters.end_date);
    if (filters.language) params.append('language', filters.language);
    if (filters.car_id) params.append('car_id', filters.car_id);

    const response = await analyticsApi.get(`/analytics/whatsapp-logs?${params.toString()}`);
    return response.data.data;
  },

  // CarFax logs (protected)
  getCarFaxLogs: async (
    page: number = 1,
    limit: number = 20,
    filters: {
      start_date?: string;
      end_date?: string;
      car_id?: string;
    } = {}
  ): Promise<PaginatedResponse<CarFaxLogEntry>> => {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
    });

    if (filters.start_date) params.append('start_date', filters.start_date);
    if (filters.end_date) params.append('end_date', filters.end_date);
    if (filters.car_id) params.append('car_id', filters.car_id);

    const response = await analyticsApi.get(`/analytics/carfax-logs?${params.toString()}`);
    return response.data.data;
  },
};

// Helper functions for creating event data
export const createEventData = (
  type: EventType,
  additionalData: Partial<EventData> = {}
): EventData => {
  return {
    type,
    page_url: window.location.href,
    referrer: document.referrer,
    device_type: getDeviceType(),
    browser_family: getBrowserFamily(),
    country_code: 'US', // Default, can be detected via IP geolocation
    timestamp: new Date(),
    ...additionalData,
  };
};

// Device detection helper
const getDeviceType = (): 'desktop' | 'mobile' | 'tablet' => {
  const userAgent = navigator.userAgent.toLowerCase();
  
  if (/tablet|ipad|playbook|silk/i.test(userAgent)) {
    return 'tablet';
  }
  
  if (/mobile|iphone|ipod|android|blackberry|opera|mini|windows\sce|palm|smartphone|iemobile/i.test(userAgent)) {
    return 'mobile';
  }
  
  return 'desktop';
};

// Browser detection helper
const getBrowserFamily = (): string => {
  const userAgent = navigator.userAgent;
  
  if (userAgent.includes('Chrome')) return 'Chrome';
  if (userAgent.includes('Firefox')) return 'Firefox';
  if (userAgent.includes('Safari')) return 'Safari';
  if (userAgent.includes('Edge')) return 'Edge';
  if (userAgent.includes('Opera')) return 'Opera';
  
  return 'Other';
};

export default analyticsService;